import React from "react";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";

/**
 * The categories page's first-load skeleton, rebuilt on the DS `Skeleton`
 * primitive (§ 62 grammar, adopted § 64): the shimmer sweep lives in the
 * primitive alone, the wait is announced exactly once through a sr-only
 * status (the page renders ONE of these — it used to mount two copies, which
 * would have meant two announcements), and the shapes are decoration.
 *
 * The tile chrome mirrors the loaded `CategoryItem` (216×56, edge border,
 * srf radius) so nothing jumps when data lands.
 */

const GROUPS = 2;
const TILES_PER_GROUP = 8;
/* Deterministic width variety — indexed, never random, so markup is stable. */
const TILE_WIDTHS = ["70%", "55%", "80%", "60%"];

const ProductCategorySkeleton: React.FC<{ "data-testid"?: string }> = ({
  "data-testid": dataTestId,
}) => {
  const baseId = dataTestId || "products-category-skeleton";
  return (
    <div aria-busy="true" data-testid={baseId}>
      <span role="status" className="tw-sr-only" data-testid={`${baseId}-status`}>
        Loading categories
      </span>
      <div aria-hidden="true" data-testid={`${baseId}-shapes`}>
        {Array.from({ length: GROUPS }, (_, g) => (
          <div key={g}>
            {/* § 268 — mirrors the real layout exactly, or the page jumps when the
                data lands: the heading's 24px above, the grid's own gutters, and no
                inset of its own (the page root owns that now). */}
            <Skeleton variant="text" width="8rem" className="tw-mt-6" />
            <div className="tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-3 tw-mt-3">
              {Array.from({ length: TILES_PER_GROUP }, (_, t) => (
                <div
                  // Static layout, not data — the index is the only identity a placeholder has.
                  // eslint-disable-next-line react/no-array-index-key
                  key={t}
                  className="tw-w-[216px] tw-h-14 tw-flex tw-items-center tw-px-4 tw-rounded-srf tw-border tw-border-edge"
                >
                  <Skeleton variant="text" width={TILE_WIDTHS[(g + t) % TILE_WIDTHS.length]} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductCategorySkeleton;
